import { Inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, of, ReplaySubject, from } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { ConfigurationProviderService } from './configuration-provider.service';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
   AccountInfo,
   AuthenticationResult,
   EventMessage,
   EventType,
   InteractionType,
   PopupRequest,
   RedirectRequest
} from '@azure/msal-browser';

import { loginRequest } from '../../auth-config';

/**
 * Set only when interactive auth has failed (redirect error / permanent failure).
 * Used to stop a deleted-Entra-user reload loop. Must NOT be set merely because
 * loginRedirect was started — that flag survives the Entra round-trip and falsely
 * trips on every successful login.
 */
const LOGIN_FAILED_KEY = 'panda.msal.loginFailed';
/** Legacy loop-guard key from an earlier fix; clear on load so it cannot poison sessions. */
const LEGACY_LOGIN_REDIRECT_ATTEMPTED_KEY = 'panda.msal.loginRedirectAttempted';

/**
 * Shape of MSAL_GUARD_CONFIG. Declared locally so emitDecoratorMetadata does not
 * require a runtime export of MsalGuardConfiguration from @azure/msal-angular
 * (that type is type-only and breaks production webpack builds).
 */
interface MsalGuardConfig {
   interactionType: InteractionType.Popup | InteractionType.Redirect;
   authRequest?: Partial<PopupRequest> | Partial<RedirectRequest>;
   loginFailedRoute?: string;
}

/**
 * Wrapper for msal.js
 */
@Injectable()
export class OidcService {
   // Replay so AuthService.login() still receives a token if redirect handling
   // finished before it subscribed (common after Entra loginRedirect).
   private accessTokenSubject = new ReplaySubject<AuthenticationResult>(1);
   accessTokenObservable = this.accessTokenSubject.asObservable();

   /** Resolves after the initial MSAL redirect response (if any) has been processed. */
   private readonly redirectHandled: Promise<AuthenticationResult | null>;

   constructor(
      private snackBar: MatSnackBar,
      private configurationProvider: ConfigurationProviderService,
      @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfig,
      private authService: MsalService,
      private msalBroadcastService: MsalBroadcastService) {
      this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window

      // Drop the old attempt-flag if present (it was set before every loginRedirect and
      // incorrectly fired the error toast after successful Entra logins).
      sessionStorage.removeItem(LEGACY_LOGIN_REDIRECT_ATTEMPTED_KEY);

      // Process redirect before any login() decision so we never race the Entra return
      // against security.run's auth.login() (that race caused the false "sign-in no longer valid" toast).
      this.redirectHandled = this.authService.handleRedirectObservable().pipe(take(1)).toPromise()
         .then((result) => {
            if (result && result.account) {
               sessionStorage.removeItem(LOGIN_FAILED_KEY);
               this.authService.instance.setActiveAccount(result.account);
               this.accessTokenSubject.next(result);
            } else {
               this.ensureActiveAccount();
            }
            return result ?? null;
         })
         .catch((error) => {
            console.error('MSAL redirect handling failed', error);
            // Real auth failure coming back from Entra (e.g. deleted user). Clear cache and
            // mark failure so we do not immediately loginRedirect again in a loop.
            this.clearLocalMsalState();
            sessionStorage.setItem(LOGIN_FAILED_KEY, '1');
            return null;
         });

      // Clear dead cached identity on permanent failures. Do NOT set LOGIN_FAILED_KEY here —
      // silent acquire often reports login_required/invalid_grant right before we intentionally
      // start interactive login; flagging that would block the interactive attempt.
      this.msalBroadcastService.msalSubject$
         .pipe(
            filter((msg: EventMessage) =>
               msg.eventType === EventType.LOGIN_FAILURE
               || msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE
               || msg.eventType === EventType.SSO_SILENT_FAILURE)
         )
         .subscribe((msg: EventMessage) => {
            console.error('MSAL failure event', msg.eventType, msg.error);
            const errorCode = (msg.error as { errorCode?: string })?.errorCode || '';
            const message = msg.error?.message || '';
            const shouldClearCache =
               /user_not_found|no_account_error|access_denied|AADSTS50034|AADSTS50053|AADSTS50126|AADSTS70043|AADSTS70008/i.test(
                  `${errorCode} ${message}`);
            if (shouldClearCache) {
               this.clearLocalMsalState();
            }
         });
   }

   login(): Observable<AuthenticationResult> {
      if (this.configurationProvider.config.bypassAuth) {
         return of('BYPASS' as any);
      }

      from(this.redirectHandled).subscribe({
         next: (redirectResult) => {
            // Redirect already delivered a token (also replayed via ReplaySubject).
            // Still re-emit so a subscriber that attached in this same login() call is covered
            // even if the ReplaySubject was cleared somehow — and so we do not start a second
            // interactive login on top of a successful Entra return.
            if (redirectResult && redirectResult.account) {
               sessionStorage.removeItem(LOGIN_FAILED_KEY);
               this.accessTokenSubject.next(redirectResult);
               return;
            }

            console.log('login(): accounts', this.authService.instance.getAllAccounts().length);

            if (this.authService.instance.getAllAccounts().length > 0) {
               this.ensureActiveAccount();
               const request = {
                  scopes: loginRequest.scopes,
                  account: this.authService.instance.getActiveAccount(),
               };
               this.authService.acquireTokenSilent(request).subscribe({
                  next: (result: AuthenticationResult) => {
                     sessionStorage.removeItem(LOGIN_FAILED_KEY);
                     this.accessTokenSubject.next(result);
                  },
                  error: (err) => {
                     console.error('silent token acquisition failed, clearing local session and re-authenticating', err);
                     // Cached account/token is no longer usable (user deleted in Entra, refresh
                     // revoked, etc.). Clear local MSAL state before interactive login so we do
                     // not bounce forever on the dead account.
                     this.reauthenticateInteractively();
                  }
               });
            } else {
               this.loginInteractively();
            }
         },
         error: (err) => {
            console.error('login() failed waiting for MSAL redirect handling', err);
            this.loginInteractively();
         }
      });

      return this.accessTokenObservable;
   }

   loginSilent(): Promise<AuthenticationResult | string> {
      if (this.configurationProvider.config.bypassAuth) {
         return of('BYPASS').toPromise();
      }

      return this.redirectHandled.then(() => {
         const account = this.ensureActiveAccount();
         if (!account) {
            return Promise.reject(new Error('No MSAL account available for silent token acquisition'));
         }

         return this.authService.acquireTokenSilent({
            scopes: loginRequest.scopes,
            account,
         }).toPromise();
      });
   }

   logout() {
      const account = this.authService.instance.getActiveAccount()
         || this.authService.instance.getAllAccounts()[0]
         || undefined;

      sessionStorage.removeItem(LOGIN_FAILED_KEY);

      try {
         if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
            this.authService.logoutPopup({
               account,
            });
         } else {
            this.authService.logoutRedirect({
               account,
            });
         }
      } catch (err) {
         console.error('MSAL logout failed; clearing local session only', err);
         this.clearLocalMsalState();
         window.location.replace('/');
      }
   }

   /**
    * Clears MSAL account/token cache in the browser without contacting Entra.
    * Used when the cached identity is invalid and IdP logout may not succeed.
    *
    * Note: @azure/msal-browser v2 has no public removeAccount() on IPublicClientApplication;
    * local-only logout + wiping msal.* localStorage keys is the supported approach.
    */
   clearLocalMsalState(): void {
      try {
         const accounts = this.authService.instance.getAllAccounts();
         // Local-only logout clears MSAL's in-memory/token cache without navigating away.
         accounts.forEach(account => {
            void this.authService.instance.logoutRedirect({
               account,
               onRedirectNavigate: () => false
            }).catch(err => console.error('Local MSAL logout failed', err));
         });
         this.authService.instance.setActiveAccount(null);
      } catch (err) {
         console.error('Failed to clear MSAL accounts', err);
      }

      // Belt-and-suspenders: remove residual MSAL keys from the configured cache location.
      this.clearMsalBrowserStorage();
   }

   private ensureActiveAccount(): AccountInfo | null {
      let account = this.authService.instance.getActiveAccount();
      if (!account) {
         const accounts = this.authService.instance.getAllAccounts();
         if (accounts.length > 0) {
            account = accounts[0];
            this.authService.instance.setActiveAccount(account);
         }
      }
      return account;
   }

   private reauthenticateInteractively(): void {
      this.clearLocalMsalState();
      this.loginInteractively(true);
   }

   private loginInteractively(forceLoginPrompt = false): void {
      // Only block if a previous interactive attempt actually failed (redirect error /
      // permanent MSAL failure). Do not treat "we started loginRedirect" as failure —
      // that flag survives the Entra round-trip and breaks every successful login.
      if (sessionStorage.getItem(LOGIN_FAILED_KEY)) {
         console.error('Previous interactive login failed; aborting to prevent reload loop');
         sessionStorage.removeItem(LOGIN_FAILED_KEY);
         this.clearLocalMsalState();
         this.snackBar.open(
            'Your sign-in is no longer valid. Please clear site data for this app or contact your administrator.',
            'Dismiss',
            { duration: 15000 });
         return;
      }

      const baseRequest = (this.msalGuardConfig.authRequest
         ? { ...this.msalGuardConfig.authRequest }
         : { ...loginRequest }) as RedirectRequest | PopupRequest;

      // Force an interactive prompt so Entra does not silently reuse a dead SSO session.
      if (forceLoginPrompt) {
         (baseRequest as RedirectRequest).prompt = 'login';
      }

      if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
         this.authService.loginPopup(baseRequest as PopupRequest)
            .subscribe({
               next: (response: AuthenticationResult) => {
                  sessionStorage.removeItem(LOGIN_FAILED_KEY);
                  this.authService.instance.setActiveAccount(response.account);
                  this.accessTokenSubject.next(response);
               },
               error: (err) => {
                  console.error('loginPopup failed', err);
                  sessionStorage.setItem(LOGIN_FAILED_KEY, '1');
                  this.clearLocalMsalState();
               }
            });
      } else {
         this.authService.loginRedirect(baseRequest as RedirectRequest);
      }
   }

   private clearMsalBrowserStorage(): void {
      // auth-config uses BrowserCacheLocation.LocalStorage for the token cache.
      // Only clear localStorage so we do not wipe in-flight redirect interaction state
      // (PKCE verifier / request) that MSAL keeps in sessionStorage.
      try {
         const keysToRemove: string[] = [];
         for (let i = 0; i < window.localStorage.length; i++) {
            const key = window.localStorage.key(i);
            if (key && key.startsWith('msal.')) {
               keysToRemove.push(key);
            }
         }
         keysToRemove.forEach(key => window.localStorage.removeItem(key));
      } catch (err) {
         console.error('Failed to clear MSAL browser storage', err);
      }
   }
}
